import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useAppContext } from './shared/context/AppContext';
import { useAnalytics } from './shared/hooks/useAnalytics';
import ProtectedRoute from './shared/components/ProtectedRoute';
import ProtectedAdminRoute from './shared/components/ProtectedAdminRoute';
import Layout from './shared/components/Layout';
import LoginScreen from './features/auth/presentation/screens/LoginScreen';
import VerifyEmailScreen from './features/auth/presentation/screens/VerifyEmailScreen';
import ForgotPasswordScreen from './features/auth/presentation/screens/ForgotPasswordScreen';
import ResetPasswordScreen from './features/auth/presentation/screens/ResetPasswordScreen';
import AuthActionScreen from './features/auth/presentation/screens/AuthActionScreen';
import TermsOfServiceScreen from './features/legal/presentation/screens/TermsOfServiceScreen';
import PrivacyPolicyScreen from './features/legal/presentation/screens/PrivacyPolicyScreen';
import PromptLibraryScreen from './features/promptLibrary/presentation/screens/PromptLibraryScreen';
import PromptLibraryLayout from './features/promptLibrary/presentation/components/PromptLibraryLayout';
import AddPromptScreen from './features/addPrompt/presentation/screens/AddPromptScreen';
import PromptDetailsScreen from './features/promptLibrary/presentation/screens/PromptDetailsScreen';
import WeeklyPlannerScreen from './features/weeklyPlan/presentation/screens/WeeklyPlannerScreen';
import SurveyScreen from './features/survey/presentation/screens/SurveyScreen';
import RoleNotSupportedScreen from './features/roleSetup/presentation/screens/RoleNotSupportedScreen';
import EditRoleScreen from './features/roleSetup/presentation/screens/EditRoleScreen';
import ContextVariablesScreen from './features/contextVariables/presentation/screens/ContextVariablesScreen';
import AdminRolesScreen from './features/adminRoles/presentation/screens/AdminRolesScreen';
import AdminRoleDetailScreen from './features/adminRoles/presentation/screens/AdminRoleDetailScreen';
import AdminRoleFormScreen from './features/adminRoles/presentation/screens/AdminRoleFormScreen';
import AdminUsersScreen from './features/adminUsers/presentation/screens/AdminUsersScreen';
import OnboardingQuestionsScreen from './features/onboardingQuestions/presentation/screens/OnboardingQuestionsScreen';
import { TutorialProvider } from './features/interactiveTutorial/presentation/context/TutorialContext';
import TutorialTooltip from './features/interactiveTutorial/presentation/components/TutorialTooltip';
import TutorialOverlay from './features/interactiveTutorial/presentation/components/TutorialOverlay';
import TutorialSkipButton from './features/interactiveTutorial/presentation/components/TutorialSkipButton';
import './App.css';

// DEBUG: This log will appear immediately when App.js loads
console.log('🚀 APP.JS LOADED - Version with Weekly Planner Debug Logging - Build:', new Date().toISOString());

// Component to track page views
function PageViewTracker() {
  const location = useLocation();
  const { user, organization } = useAppContext();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname, user, organization);
  }, [location.pathname, user, organization, trackPageView]);

  return null;
}

function App() {
  return (
    <AppProvider>
      <Router>
        <PageViewTracker />
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/verify-email" element={<VerifyEmailScreen />} />
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          <Route path="/auth/action" element={<AuthActionScreen />} />
          <Route path="/terms-of-service" element={<TermsOfServiceScreen />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <TutorialProvider>
                  <TutorialOverlay />
                  <TutorialTooltip />
                  <TutorialSkipButton />
                  <Layout>
                    <Routes>
                    <Route path="/onboarding-questions" element={<OnboardingQuestionsScreen />} />
                    <Route path="/role-not-supported" element={<RoleNotSupportedScreen />} />
                    <Route path="/edit-role" element={<EditRoleScreen />} />
                    <Route path="/home" element={<PromptLibraryLayout><PromptLibraryScreen /></PromptLibraryLayout>} />
                    <Route path="/add-prompt" element={<AddPromptScreen />} />
                    <Route path="/prompt/:id" element={<PromptDetailsScreen />} />
                    <Route path="/weekly-planner" element={<WeeklyPlannerScreen />} />
                    <Route path="/survey" element={<SurveyScreen />} />
                    <Route path="/context-variables" element={<ContextVariablesScreen />} />
                    <Route path="*" element={<Navigate to="/home" replace />} />
                  </Routes>
                </Layout>
                </TutorialProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedAdminRoute>
                <Layout>
                  <Routes>
                    <Route path="/roles" element={<AdminRolesScreen />} />
                    <Route path="/roles/:roleId" element={<AdminRoleDetailScreen />} />
                    <Route path="/roles/new" element={<AdminRoleFormScreen />} />
                    <Route path="/users" element={<AdminUsersScreen />} />
                    <Route path="*" element={<Navigate to="/admin/roles" replace />} />
                  </Routes>
                </Layout>
              </ProtectedAdminRoute>
            }
          />
        </Routes>
      </Router>
    </AppProvider>
  );
}

export default App;
